import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { PageHeader } from "@/components/ui/page-header";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from "@/components/ui/pagination";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TailoredDocumentService, TailoredDocumentSession } from "@/services/tailoredDocumentService";
import { BulkLetterService, BulkLetterData, BulkLetterHeaderInfo } from "@/services/bulkLetterService";
import { supabase } from "@/integrations/supabase/client";
import { ResumeMatchBreakdown } from './ResumeMatchBreakdown';
import { FileText, Mail, Heart, User, Linkedin, Send, Calendar, Building2, Briefcase, Loader2, Trash2, Eye, Download, BarChart3, Monitor, FileJson, ArrowUpDown, TrendingUp, CheckCircle, Target, Star, ChevronLeft, ChevronRight, FileType, Edit3, ArrowLeft } from 'lucide-react';
interface TailoredDocumentLibraryProps {
  onBack: () => void;
  onUploadNew?: () => void;
}
const getDocumentIcon = (type: string) => {
  switch (type) {
    case 'resume':
      return <FileText className="w-4 h-4" />;
    case 'cover_letter':
      return <Mail className="w-4 h-4" />;
    case 'thank_you_letter':
      return <Heart className="w-4 h-4" />;
    case 'executive_bio':
      return <User className="w-4 h-4" />;
    case 'linkedin_profile':
      return <Linkedin className="w-4 h-4" />;
    case 'outreach_letter':
      return <Send className="w-4 h-4" />;
    default:
      return <FileText className="w-4 h-4" />;
  }
};
const getDocumentTypeLabel = (type: string) => {
  switch (type) {
    case 'resume':
      return 'Resume';
    case 'cover_letter':
      return 'Cover Letter';
    case 'thank_you_letter':
      return 'Thank You Letter';
    case 'executive_bio':
      return 'Executive Bio';
    case 'linkedin_profile':
      return 'LinkedIn Profile';
    case 'outreach_letter':
      return 'Outreach Letter';
    default:
      return type;
  }
};
export function TailoredDocumentLibrary({
  onBack,
  onUploadNew
}: TailoredDocumentLibraryProps) {
  const [sessions, setSessions] = useState<TailoredDocumentSession[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState<string | null>(null);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState<string | null>(null);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [selectedSessions, setSelectedSessions] = useState<Set<string>>(new Set());
  const [sortBy, setSortBy] = useState<string>('date-desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [isGeneratingLetters, setIsGeneratingLetters] = useState<string | null>(null);
  const [isGeneratingBulkLetters, setIsGeneratingBulkLetters] = useState(false);
  const {
    toast
  } = useToast();
  const {
    user,
    loading: authLoading
  } = useAuth();
  const navigate = useNavigate();
  // Load once auth has settled. If auth resolves to no user, stop the spinner
  // immediately instead of waiting forever. Without this guard the component
  // sits at "Loading…" indefinitely when the auth state never reports a user.
  useEffect(() => {
    if (authLoading) return;
    if (user) {
      loadTailoredSessions();
    } else {
      setIsLoading(false);
    }
  }, [user, authLoading]);

  // Safety net: if the Supabase fetch never resolves (rare, but observed),
  // cut off the spinner after 20s so the user sees the empty state instead of
  // a frozen page. Surfaces a toast so they know what happened.
  useEffect(() => {
    if (!isLoading) return;
    const timer = setTimeout(() => {
      setIsLoading((prev) => {
        if (prev) {
          console.warn('[TailoredDocumentLibrary] Load timed out after 20s — showing empty state.');
          toast({
            title: 'Took longer than expected',
            description: 'Could not finish loading your tailored documents. Please refresh to retry.',
            variant: 'destructive',
          });
          return false;
        }
        return prev;
      });
    }, 20000);
    return () => clearTimeout(timer);
  }, [isLoading, toast]);
  const sortSessions = (sessions: TailoredDocumentSession[], sortBy: string): TailoredDocumentSession[] => {
    const sorted = [...sessions];
    switch (sortBy) {
      case 'date-desc':
        return sorted.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      case 'date-asc':
        return sorted.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
      case 'job-title-asc':
        return sorted.sort((a, b) => (a.job_title || '').localeCompare(b.job_title || ''));
      case 'job-title-desc':
        return sorted.sort((a, b) => (b.job_title || '').localeCompare(a.job_title || ''));
      case 'company-asc':
        return sorted.sort((a, b) => (a.company_name || '').localeCompare(b.company_name || ''));
      case 'company-desc':
        return sorted.sort((a, b) => (b.company_name || '').localeCompare(a.company_name || ''));
      case 'score-desc':
        return sorted.sort((a, b) => (b.resume_document?.match_score || 0) - (a.resume_document?.match_score || 0));
      case 'score-asc':
        return sorted.sort((a, b) => (a.resume_document?.match_score || 0) - (b.resume_document?.match_score || 0));
      case 'improvement-desc':
        return sorted.sort((a, b) => getImprovement(b) - getImprovement(a));
      case 'improvement-asc':
        return sorted.sort((a, b) => getImprovement(a) - getImprovement(b));
      default:
        return sorted;
    }
  };
  const sortedSessions = sortSessions(sessions, sortBy);

  // Pagination calculations
  const totalPages = Math.ceil(sortedSessions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedSessions = sortedSessions.slice(startIndex, endIndex);
  console.log('TailoredDocumentLibrary Pagination Debug:', {
    totalSessions: sortedSessions.length,
    itemsPerPage,
    totalPages,
    currentPage
  });

  // Reset page when sortBy changes
  useEffect(() => {
    setCurrentPage(1);
  }, [sortBy]);
  const loadTailoredSessions = async () => {
    try {
      setIsLoading(true);
      const response = await TailoredDocumentService.getTailoredDocumentSessions();
      setSessions(response);
    } catch (error) {
      console.error('Error loading tailored sessions:', error);
      toast({
        title: "Error",
        description: "Failed to load tailored sessions. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Extract personal information from session data
  const extractPersonalInfoFromSession = (session: TailoredDocumentSession) => {
    let name = '';
    let email = '';
    let phone = '';

    // Try to get from parsed resume data first
    if (session.resume_document?.parsed_document_data) {
      const personalInfo = session.resume_document.parsed_document_data.personal_info;
      if (personalInfo) {
        name = personalInfo.name || personalInfo.full_name || '';
        email = personalInfo.email || '';
        phone = personalInfo.phone || personalInfo.phone_number || '';
      }
    }

    // Fallback to parsing from content
    if (!name || !email || !phone) {
      const content = session.resume_document?.content || '';
      const lines = content.split('\n').slice(0, 10); // Check first 10 lines

      for (const line of lines) {
        // Extract email
        if (!email && line.includes('@')) {
          const emailMatch = line.match(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/);
          if (emailMatch) email = emailMatch[0];
        }

        // Extract phone
        if (!phone) {
          const phoneMatch = line.match(/(\(?\d{3}\)?[-.\s]?)?\d{3}[-.\s]?\d{4}/);
          if (phoneMatch) phone = phoneMatch[0];
        }

        // Extract name (first non-empty line that's not email/phone)
        if (!name && line.trim() && !line.includes('@') && !line.match(/(\(?\d{3}\)?[-.\s]?)?\d{3}[-.\s]?\d{4}/)) {
          name = line.trim();
        }
      }
    }

    return {
      name: name || 'Professional',
      email: email || 'professional@email.com',
      phone: phone || '(555) 123-4567'
    };
  };

  // Clean letter content by removing redundant header information
  const cleanLetterContent = (content: string, session: TailoredDocumentSession): string => {
    if (!content) return '';
    
    const lines = content.split('\n');
    const cleanedLines = [];
    let skipLines = true;
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      
      // Skip common header patterns
      if (skipLines) {
        // Skip name, contact info, date, company info at the beginning
        if (line === '' || 
            line.match(/^[A-Za-z\s]+$/) && line.length < 50 || // Name lines
            line.includes('@') || // Email
            line.match(/\(\d{3}\)\s?\d{3}-\d{4}/) || // Phone
            line.match(/^\d{1,2}\/\d{1,2}\/\d{4}/) || // Date
            line.match(/^[A-Za-z\s,]+,\s?[A-Z]{2}\s?\d{5}/) || // Address
            line.includes('linkedin.com') ||
            line.toLowerCase().includes('dear ') ||
            line.toLowerCase().includes('hiring manager') ||
            line === session.company_name
        ) {
          continue;
        }
        // Start including content when we hit the actual letter body
        skipLines = false;
      }
      
      // Skip closing signatures at the end
      if (line.toLowerCase().includes('sincerely') || 
          line.toLowerCase().includes('best regards') ||
          line.toLowerCase().includes('yours truly')) {
        break;
      }
      
      cleanedLines.push(line);
    }
    
    return cleanedLines.join('\n').trim();
  };

  // Transform session data to letter format and determine available letters
  const transformSessionToLetterData = (session: TailoredDocumentSession): { letterData: BulkLetterData, availableLetters: { coverLetter: boolean, thankYouLetter: boolean, outreachLetter: boolean } } => {
    const personalInfo = extractPersonalInfoFromSession(session);
    
    // Extract real letter content from session documents
    const coverLetterDoc = session.documents.find(doc => doc.document_type === 'cover_letter');
    const thankYouLetterDoc = session.documents.find(doc => doc.document_type === 'thank_you_letter');
    const outreachLetterDoc = session.documents.find(doc => doc.document_type === 'outreach_letter');
    
    // Determine which letters are available
    const availableLetters = {
      coverLetter: !!coverLetterDoc?.content,
      thankYouLetter: !!thankYouLetterDoc?.content,
      outreachLetter: !!outreachLetterDoc?.content
    };
    
    // Clean and use only real content (no fallbacks for missing letters)
    const coverLetterBody = coverLetterDoc?.content ? cleanLetterContent(coverLetterDoc.content, session) : '';
    const thankYouLetterBody = thankYouLetterDoc?.content ? cleanLetterContent(thankYouLetterDoc.content, session) : '';
    const outreachLetterBody = outreachLetterDoc?.content ? cleanLetterContent(outreachLetterDoc.content, session) : '';

    const letterData = {
      headerInfo: {
        fullName: personalInfo.name,
        phoneNumber: personalInfo.phone,
        email: personalInfo.email,
        cityST: 'Your City, ST',
        linkedin: 'linkedin.com/in/yourprofile',
        companyName: session.company_name || 'Company Name',
        recipientName: 'Hiring Manager',
        jobTitle: session.job_title || 'Position'
      },
      coverLetterBody,
      thankYouLetterBody,
      outreachLetterBody,
      resumeIdentifier: session.session_id
    };

    return { letterData, availableLetters };
  };

  // Generate letters for individual session
  const handleGenerateLetters = async (session: TailoredDocumentSession) => {
    if (!session.resume_document?.content && !session.resume_document?.parsed_document_data) {
      toast({
        title: "Missing Data",
        description: "Resume content is required for letter generation.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsGeneratingLetters(session.session_id);
      
      toast({
        title: "Generating Letters",
        description: "Creating personalized letters based on your resume...",
      });

      const { letterData, availableLetters } = transformSessionToLetterData(session);
      
      // Check if any letters are available
      const hasAnyLetters = Object.values(availableLetters).some(Boolean);
      if (!hasAnyLetters) {
        toast({
          title: "No Letters Available",
          description: "No letter content found for this session. Please create letters first.",
          variant: "destructive",
        });
        return;
      }
      
      const request = {
        letters: [letterData],
        letterTypes: availableLetters
      };

      const response = await BulkLetterService.generateAndStoreBulkLetters(request);

      if (!response.success) {
        throw new Error('Generation failed');
      }

      const personalInfo = extractPersonalInfoFromSession(session);
      
      toast({
        title: "Letters Generated Successfully",
        description: `Generated ${response.files.length} personalized letters.`,
      });

      // Navigate to Word Document Viewer
      navigate('/word-document-viewer', {
        state: {
          bulkLetters: response.files,
          source: 'tailored-library'
        }
      });

    } catch (error) {
      console.error('Letter generation error:', error);
      toast({
        title: "Generation Failed",
        description: error instanceof Error ? error.message : 'Failed to generate letters',
        variant: "destructive",
      });
    } finally {
      setIsGeneratingLetters(null);
    }
  };

  // Generate letters for selected sessions
  const handleGenerateBulkLetters = async () => {
    if (selectedSessions.size === 0) {
      toast({
        title: "No Sessions Selected",
        description: "Please select at least one session to generate letters for.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsGeneratingBulkLetters(true);
      
      toast({
        title: "Generating Letters",
        description: `Creating personalized letters for ${selectedSessions.size} selected sessions...`,
      });

      const selectedSessionData = sessions.filter(session => selectedSessions.has(session.session_id));
      const transformedData = selectedSessionData.map(session => transformSessionToLetterData(session));
      const letterDataArray = transformedData.map(item => item.letterData);

      // Determine which letter types are available across all sessions
      const availableLetterTypes = {
        coverLetter: transformedData.some(item => item.availableLetters.coverLetter),
        thankYouLetter: transformedData.some(item => item.availableLetters.thankYouLetter),
        outreachLetter: transformedData.some(item => item.availableLetters.outreachLetter)
      };

      // Check if any letters are available
      const hasAnyLetters = Object.values(availableLetterTypes).some(Boolean);
      if (!hasAnyLetters) {
        toast({
          title: "No Letters Available",
          description: "Selected sessions don't have any letter content. Please create letters first.",
          variant: "destructive",
        });
        return;
      }

      const request = {
        letters: letterDataArray,
        letterTypes: availableLetterTypes
      };

      const response = await BulkLetterService.generateAndStoreBulkLetters(request);

      if (!response.success) {
        throw new Error('Bulk generation failed');
      }

      toast({
        title: "Letters Generated Successfully",
        description: `Generated ${response.files.length} personalized letters for ${selectedSessions.size} sessions.`,
      });

      // Navigate to Word Document Viewer
      navigate('/word-document-viewer', {
        state: {
          bulkLetters: response.files,
          source: 'tailored-library'
        }
      });

      // Clear selections after successful generation
      setSelectedSessions(new Set());

    } catch (error) {
      console.error('Bulk letter generation error:', error);
      toast({
        title: "Generation Failed",
        description: error instanceof Error ? error.message : 'Failed to generate letters',
        variant: "destructive",
      });
    } finally {
      setIsGeneratingBulkLetters(false);
    }
  };
  const handleDeleteSession = async (sessionId: string) => {
    try {
      setIsDeleting(sessionId);
      await TailoredDocumentService.deleteTailoredDocumentSession(sessionId);
      await loadTailoredSessions();
      toast({
        title: "Session deleted",
        description: "The tailoring session and all its documents have been deleted."
      });
    } catch (error) {
      console.error('Error deleting session:', error);
      toast({
        title: "Error",
        description: "Failed to delete session. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsDeleting(null);
      setShowDeleteDialog(null);
    }
  };
  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  const getScoreColor = (score?: number): string => {
    if (!score) return "text-gray-500";
    if (score >= 86) return "text-green-600";
    if (score >= 70) return "text-yellow-600";
    if (score >= 31) return "text-orange-600";
    return "text-red-600";
  };
  const getScoreLabel = (score?: number): string => {
    if (!score) return "No Score";
    if (score >= 86) return "Excellent";
    if (score >= 70) return "Good";
    if (score >= 31) return "Fair";
    return "Poor";
  };
  const getImprovement = (session: TailoredDocumentSession): number => {
    const resume = session.resume_document;
    if (!resume || !resume.match_score || !resume.original_match_score) return 0;
    return resume.match_score - resume.original_match_score;
  };
  const getImprovementColor = (improvement: number): string => {
    if (improvement > 0) return "text-green-600";
    if (improvement < 0) return "text-red-600";
    return "text-gray-500";
  };
  const getImprovementLabel = (improvement: number): string => {
    if (improvement > 0) return `+${improvement} points`;
    if (improvement < 0) return `${improvement} points`;
    return "No change";
  };
  const handleSelectSession = (sessionId: string, checked: boolean) => {
    const newSelected = new Set(selectedSessions);
    if (checked) {
      newSelected.add(sessionId);
    } else {
      newSelected.delete(sessionId);
    }
    setSelectedSessions(newSelected);
  };
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedSessions(new Set(sortedSessions.map(session => session.session_id)));
    } else {
      setSelectedSessions(new Set());
    }
  };
  const handleBulkDelete = async () => {
    try {
      setIsBulkDeleting(true);
      const deletePromises = Array.from(selectedSessions).map(sessionId => TailoredDocumentService.deleteTailoredDocumentSession(sessionId));
      await Promise.all(deletePromises);
      toast({
        title: "Sessions Deleted",
        description: `${selectedSessions.size} tailoring session(s) have been removed.`
      });

      // Clear selections
      setSelectedSessions(new Set());
      setShowBulkDeleteDialog(false);

      // Refresh the sessions
      await loadTailoredSessions();
    } catch (error) {
      console.error('Error deleting sessions:', error);
      toast({
        title: "Bulk Delete Failed",
        description: "Failed to delete some sessions. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsBulkDeleting(false);
    }
  };
  const handleDownloadSessionJSON = async (session: TailoredDocumentSession) => {
    try {
      // Clean, minimal JSON structure - no duplicate content
      const jsonData = {
        session_id: session.session_id,
        job_title: session.job_title,
        company_name: session.company_name,
        created_at: session.created_at,
        match_scores: {
          original: session.resume_document?.original_match_score,
          tailored: session.resume_document?.match_score
        },
        document_ids: {
          resume: session.resume_document?.id,
          cover_letter: session.cover_letter_document?.id,
          thank_you_letter: session.thank_you_letter_document?.id,
          executive_bio: session.executive_bio_document?.id,
          linkedin_profile: session.linkedin_profile_document?.id,
          outreach_letter: session.outreach_letter_document?.id
        }
      };

      // Save JSON to Supabase storage
      const fileName = `${session.job_title}_${session.company_name}_${new Date().getTime()}.json`;
      const blob = new Blob([JSON.stringify(jsonData, null, 2)], {
        type: 'application/json'
      });
      const {
        data,
        error
      } = await supabase.storage.from('client-documents').upload(`tailored-sessions/${user?.id}/${fileName}`, blob, {
        cacheControl: '3600',
        upsert: true
      });
      if (error) {
        throw error;
      }
      toast({
        title: "Session Metadata Saved",
        description: "Clean session data saved to your account."
      });
    } catch (error) {
      console.error('Error saving session data:', error);
      toast({
        title: "Save Failed",
        description: "Failed to save session data to your account.",
        variant: "destructive"
      });
    }
  };
  const handleOpenSessionInWordViewer = (session: TailoredDocumentSession) => {
    if (!session.documents || session.documents.length === 0) {
      toast({
        title: "No Documents",
        description: "This session has no documents to view in the Word editor.",
        variant: "destructive"
      });
      return;
    }

    // Prepare documents with JSON data for Word generation
    const multipleDocuments = session.documents.map(doc => ({
      resumeJson: doc.document_json_data || doc.content, // Use JSON data if available, fallback to content
      tailoringSessionId: session.session_id,
      tailoredDocumentId: doc.id,
      documentType: doc.document_type || 'resume'
    }));

    // Navigate to Word viewer with JSON data for proper Word generation
    navigate('/word-document-viewer', {
      state: {
        multipleDocuments,
        fileName: `${session.job_title} - ${session.company_name} (${session.documents.length} docs)`,
        sessionId: session.session_id
      }
    });
    
    toast({
      title: "Opening in Word Viewer",
      description: `${session.documents.length} document(s) from this session will be generated as Word documents.`
    });
  };
  const handleOpenSelectedInWordViewer = () => {
    const selectedSessionObjects = sessions.filter(s => selectedSessions.has(s.session_id));
    const allDocuments = selectedSessionObjects.flatMap(s => s.documents);
    if (allDocuments.length === 0) {
      toast({
        title: "No Documents",
        description: "Selected sessions don't have documents to view.",
        variant: "destructive"
      });
      return;
    }

    // Prepare documents with JSON data for Word generation
    const multipleDocuments = allDocuments.map(doc => {
      const parentSession = selectedSessionObjects.find(s => s.documents.some(d => d.id === doc.id));
      return {
        resumeJson: doc.document_json_data || doc.content, // Use JSON data if available, fallback to content
        tailoringSessionId: parentSession?.session_id,
        tailoredDocumentId: doc.id,
        documentType: doc.document_type || 'resume'
      };
    });

    // Navigate to Word viewer with JSON data for proper Word generation
    navigate('/word-document-viewer', {
      state: {
        multipleDocuments,
        fileName: `${selectedSessions.size} Selected Sessions (${allDocuments.length} docs)`,
        sessionId: selectedSessionObjects[0]?.session_id // Use first session ID as reference
      }
    });
    toast({
      title: "Opening Selected Documents",
      description: `${allDocuments.length} document(s) from ${selectedSessions.size} session(s) are being loaded in the Word viewer.`
    });
  };
  if (!user) {
    return <div className="min-h-screen flex items-center justify-center">
        <Card className="w-96">
          <CardHeader>
            <CardTitle>Authentication Required</CardTitle>
            <CardDescription>Please log in to access your tailored documents.</CardDescription>
          </CardHeader>
        </Card>
      </div>;
  }
  if (isLoading) {
    return <div className="min-h-screen flex items-center justify-center">
        <div className="flex items-center gap-2">
          <Loader2 className="w-6 h-6 animate-spin" />
          <span>Loading your tailored documents...</span>
        </div>
      </div>;
  }
  return (
    <div className="min-h-screen bg-background">
      {/* Custom Header matching resume tailoring page */}
      <div className="bg-gradient-to-r from-accent-light/30 to-primary-light/20 border-b border-border shadow-sm backdrop-blur-sm">
        <div className="container mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={onBack}
                className="h-9 w-9 p-0"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div className="space-y-1">
                <h1 className="text-2xl font-bold text-foreground">Tailored Documents</h1>
                <p className="text-sm text-muted-foreground">AI-customized documents organized by tailoring session</p>
              </div>
            </div>
            <div className="flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-primary/10 to-accent/10 rounded-full border border-primary/20 shadow-sm">
              <span className="text-xs font-medium text-muted-foreground">powered by</span>
              <span className="text-sm font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">TailorWiz</span>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto py-6 space-y-6">
        {/* Centered gradient headline */}
        <div className="text-center mb-8">
          <h2 className="text-4xl font-bold mb-4">
            <span className="bg-gradient-to-r from-primary via-accent to-primary-hover bg-clip-text text-transparent">
              Your Tailored Document Library
            </span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Manage and access all your AI-tailored documents in one place. Each session contains resumes and cover letters customized for specific job applications.
          </p>
        </div>
        {sessions.length === 0 ? <Card>
            <CardContent className="p-8 text-center">
              <FileText className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-xl font-semibold mb-2">No Tailored Documents</h3>
              <p className="text-muted-foreground mb-6">
                You haven't created any tailored documents yet. Start by tailoring a resume to a specific job.
              </p>
              <Button onClick={() => window.location.href = '/resume-tailoring'}>
                Start Tailoring
              </Button>
            </CardContent>
          </Card> : <div className="space-y-6">
            {/* Controls Header with Bulk Actions */}
            <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-4">
                {/* Bulk Selection */}
                <div className="flex items-center gap-3">
                  <Checkbox checked={selectedSessions.size === sortedSessions.length && sortedSessions.length > 0} onCheckedChange={checked => handleSelectAll(checked as boolean)} />
                  <span className="text-sm font-medium">
                    {selectedSessions.size === 0 ? "Select All" : `${selectedSessions.size} of ${sortedSessions.length} selected`}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-4">
                {/* Sort Controls */}
                <div className="flex items-center gap-2">
                  <ArrowUpDown className="w-4 h-4 text-muted-foreground" />
                  <Select value={sortBy} onValueChange={setSortBy}>
                    <SelectTrigger className="w-52">
                      <SelectValue placeholder="Sort by..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="date-desc">Date (Newest First)</SelectItem>
                      <SelectItem value="date-asc">Date (Oldest First)</SelectItem>
                      <SelectItem value="job-title-asc">Job Title (A-Z)</SelectItem>
                      <SelectItem value="job-title-desc">Job Title (Z-A)</SelectItem>
                      <SelectItem value="company-asc">Company (A-Z)</SelectItem>
                      <SelectItem value="company-desc">Company (Z-A)</SelectItem>
                      <SelectItem value="score-desc">Match Score (High-Low)</SelectItem>
                      <SelectItem value="score-asc">Match Score (Low-High)</SelectItem>
                      <SelectItem value="improvement-desc">Improvement (High-Low)</SelectItem>
                      <SelectItem value="improvement-asc">Improvement (Low-High)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Bulk Actions for Selected Items */}
              {selectedSessions.size > 0 && <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={handleOpenSelectedInWordViewer} className="gap-2 bg-blue-600 hover:bg-blue-700 text-white border-blue-500">
                    <FileType className="w-4 h-4" />
                    Open Selected ({selectedSessions.size}) in Microsoft Word
                  </Button>
                  <Button
                    onClick={handleGenerateBulkLetters}
                    disabled={isGeneratingBulkLetters || selectedSessions.size === 0}
                    variant="outline"
                    size="sm"
                    className="gap-2 bg-green-600 hover:bg-green-700 text-white border-green-500"
                  >
                    {isGeneratingBulkLetters ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Mail className="h-4 w-4" />
                        Generate Letters for Selected ({selectedSessions.size})
                      </>
                    )}
                  </Button>
                  <Button variant="destructive" size="sm" onClick={() => setShowBulkDeleteDialog(true)} disabled={isBulkDeleting} className="gap-2">
                    {isBulkDeleting ? <div className="w-4 h-4 border border-current border-t-transparent rounded-full animate-spin" /> : <Trash2 className="w-4 h-4" />}
                    Delete Selected ({selectedSessions.size})
                  </Button>
                </div>}
            </div>
            

            <div className="grid gap-6 max-w-6xl mx-auto">
              {paginatedSessions.map(session => {
            const resume = session.resume_document;
            const improvement = getImprovement(session);
            return <Card key={session.session_id} className="border-2 border-gray-200 shadow-lg bg-gradient-to-r from-background to-muted/20 hover:shadow-xl hover:border-primary/40 transition-all duration-300 transform hover:scale-[1.01]">
                    <CardContent className="p-8">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-6">
                          <Checkbox checked={selectedSessions.has(session.session_id)} onCheckedChange={checked => handleSelectSession(session.session_id, checked as boolean)} className="mt-1 scale-110" />
                          
                          {/* Enhanced Circular Match Score */}
                          {/* A session with no computed score shows a neutral badge —
                              never the red "Poor" circle, which reads as a zero. */}
                          {resume && typeof resume.match_score !== 'number' && <div className="flex-shrink-0 flex flex-col items-center relative pt-6">
                              <div className="w-24 h-24 rounded-full border-4 border-slate-300 bg-slate-50 text-slate-400 flex flex-col items-center justify-center mt-6">
                                <div className="text-2xl font-bold">—</div>
                                <div className="text-xs font-semibold leading-tight">No score</div>
                              </div>
                            </div>}
                          {resume && typeof resume.match_score === 'number' && <div className="flex-shrink-0 flex flex-col items-center relative pt-6">
                              {/* Score improvement indicator centered over the circle */}
                              {resume.original_match_score !== undefined && improvement > 0 && <div className="absolute top-2 left-[calc(50%-8px)] -translate-x-1/2 z-10 bg-green-50 rounded-full border border-blue-800 px-2 py-1 shadow-md mx-[5px]">
                                  <div className="flex items-center whitespace-nowrap">
                                    <TrendingUp className="w-4 h-4 text-blue-800 mr-1" />
                                    <span className="text-sm font-bold text-blue-800">+{improvement} Increase!</span>
                                  </div>
                                </div>}
                              
                              <div className={`w-24 h-24 rounded-full border-4 flex flex-col items-center justify-center shadow-lg transition-all duration-300 hover:scale-105 mt-6 ${resume.match_score >= 80 ? "text-green-600 border-green-600 bg-green-50 shadow-green-200/50" : resume.match_score >= 56 ? "text-yellow-600 border-yellow-600 bg-yellow-50 shadow-yellow-200/50" : resume.match_score >= 30 ? "text-orange-600 border-orange-600 bg-orange-50 shadow-orange-200/50" : "text-red-600 border-red-600 bg-red-50 shadow-red-200/50"}`}>
                                <div className="text-2xl font-bold">{resume.match_score}%</div>
                                <div className="text-xs font-semibold opacity-90 leading-tight">
                                  {resume.match_score >= 80 ? "Excellent" : resume.match_score >= 56 ? "Good" : resume.match_score >= 30 ? "Fair" : "Poor"}
                                </div>
                              </div>
                            </div>}
                          
                          <div className="space-y-2 flex-1 min-w-0">
                            <div className="flex items-start gap-3">
                              <Briefcase className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                              <div className="flex-1">
                                <CardTitle className="text-xl font-bold text-primary hover:text-primary/80 transition-colors leading-tight">{session.job_title}</CardTitle>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <Building2 className="w-5 h-5 text-muted-foreground" />
                              <CardDescription className="text-lg font-semibold text-gray-700">{session.company_name}</CardDescription>
                            </div>
                            <div className="flex items-center gap-3">
                              <Calendar className="w-5 h-5 text-muted-foreground" />
                              <CardDescription className="text-sm text-gray-600">{formatDate(session.created_at)}</CardDescription>
                            </div>
                            
                            {/* Success Badge */}
                            <div className="flex items-center gap-2 mt-3">
                              <Badge className="bg-green-100 text-green-800 px-3 py-1.5 text-sm font-semibold border border-green-300 shadow-sm">
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Successfully Tailored!
                              </Badge>
                            </div>
                          </div>
                        </div>
                        
                        {/* Enhanced Documents and Actions Section */}
                        <div className="flex items-center gap-6">
                          {/* Document Types */}
                          <div className="space-y-3 border-l-2 border-gray-200 pl-6">
                            <h5 className="text-sm font-bold text-gray-700 flex items-center gap-2">
                              <FileText className="w-5 h-5 text-primary" />
                              Tailored Documents ({session.documents.length})
                            </h5>
                            <div className="flex flex-wrap gap-2 max-w-xs">
                              {session.documents.map(doc => <Badge key={doc.id} className="bg-white border-2 border-primary/40 text-primary hover:bg-primary hover:text-white transition-all duration-200 text-xs px-3 py-2 font-medium shadow-sm hover:shadow-md">
                                  {getDocumentIcon(doc.document_type)}
                                  <span className="ml-2">{getDocumentTypeLabel(doc.document_type)}</span>
                                </Badge>)}
                            </div>
                          </div>
                          
                          {/* Enhanced Action Buttons */}
                          <div className="flex flex-col gap-3 border-l-2 border-gray-200 pl-6">
                            {/* User Name Badge - positioned above View Full Documents button */}
                            {session.user_name && <div>
                                <Badge variant="outline" className="bg-green-50 border-green-300 text-green-800 font-medium">
                                  <Star className="w-4 h-4 mr-1 text-green-600" />
                                  {session.user_name}
                                </Badge>
                              </div>}
                            
                            <Button onClick={() => navigate(`/tailored-document/${session.session_id}`, {
                        state: {
                          session
                        }
                      })} className="bg-primary hover:bg-primary/90 text-white font-semibold px-6 py-3 shadow-lg hover:shadow-xl transition-all duration-200 text-sm transform hover:scale-105">
                              <Eye className="w-4 h-4 mr-2" />
                              View Full Documents
                            </Button>
                            
                            {/* Reopen the full results screen (scores, tailoring
                                analysis, template picker, Perfect Fit / Layout QA)
                                exactly as it appeared after tailoring finished. */}
                            <Button onClick={() => navigate(`/resume-results-templated?sessionId=${session.session_id}`)} className="bg-[#0B1C3F] hover:bg-[#16305f] text-white font-semibold px-6 py-3 shadow-lg hover:shadow-xl transition-all duration-200 text-sm transform hover:scale-105 border-2 border-[#C9A84C]">
                              <BarChart3 className="w-4 h-4 mr-2" />
                              Open Results Studio
                            </Button>

                            {/* Prominent Microsoft Word Button */}
                            <Button onClick={() => handleOpenSessionInWordViewer(session)} className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 shadow-lg hover:shadow-xl transition-all duration-200 text-sm transform hover:scale-105 border-2 border-blue-500">
                              <FileType className="w-4 h-4 mr-2" />
                              View & Edit in Microsoft Word
                            </Button>
                            
                            <Button
                              onClick={() => handleGenerateLetters(session)}
                              disabled={isGeneratingLetters === session.session_id}
                              className="bg-green-600 hover:bg-green-700 text-white font-semibold px-6 py-3 shadow-lg hover:shadow-xl transition-all duration-200 text-sm transform hover:scale-105 border-2 border-green-500"
                            >
                              {isGeneratingLetters === session.session_id ? (
                                <>
                                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                  Generating...
                                </>
                              ) : (
                                <>
                                  <Mail className="h-4 w-4 mr-2" />
                                  Generate Letters
                                </>
                              )}
                            </Button>
                            
                            <div className="flex gap-3">
                              <Button variant="outline" size="sm" onClick={() => setShowDeleteDialog(session.session_id)} disabled={isDeleting === session.session_id} className="gap-2 text-destructive hover:text-destructive border-red-300 hover:bg-red-50 hover:border-red-400 text-xs px-4 py-2 font-medium shadow-sm hover:shadow-md transition-all duration-200">
                                {isDeleting === session.session_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                                Delete Session
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>;
          })}
            </div>
            
            {/* Pagination Controls */}
            {totalPages > 1 && <div className="flex items-center justify-between mt-6">
                <div className="text-sm text-muted-foreground">
                  Showing {startIndex + 1}-{Math.min(endIndex, sortedSessions.length)} of {sortedSessions.length} sessions
                </div>
                
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} disabled={currentPage === 1} className="gap-2">
                    <ChevronLeft className="w-4 h-4" />
                    Previous
                  </Button>
                  
                  <div className="flex items-center gap-1">
                    {Array.from({
                length: totalPages
              }, (_, i) => i + 1).map(page => <Button key={page} variant={currentPage === page ? "default" : "outline"} size="sm" onClick={() => setCurrentPage(page)} className="w-8 h-8 p-0">
                        {page}
                      </Button>)}
                  </div>
                  
                  <Button variant="outline" size="sm" onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))} disabled={currentPage === totalPages} className="gap-2">
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>}
          </div>}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!showDeleteDialog} onOpenChange={open => !open && setShowDeleteDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Tailoring Session</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete all documents in this tailoring session. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => showDeleteDialog && handleDeleteSession(showDeleteDialog)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete Session
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Confirmation Dialog */}
      <AlertDialog open={showBulkDeleteDialog} onOpenChange={setShowBulkDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Selected Sessions</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {selectedSessions.size} selected tailoring session{selectedSessions.size !== 1 ? 's' : ''}? 
              This will permanently remove all documents in these sessions. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleBulkDelete} disabled={isBulkDeleting} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {isBulkDeleting ? <div className="w-4 h-4 border border-current border-t-transparent rounded-full animate-spin mr-2" /> : null}
              Delete {selectedSessions.size} Session{selectedSessions.size !== 1 ? 's' : ''}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}